import React, { useState } from "react";
import {
  Stack,
  IconButton,
  Tooltip,
  Card,
  Checkbox,
  styled,
  Divider,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import RemoveCircleOutlineOutlinedIcon from "@mui/icons-material/RemoveCircleOutlineOutlined";
import { WarningAmberOutlined as WarningAmberOutlinedIcon } from "@mui/icons-material";
import { Sync as SyncIcon } from "@mui/icons-material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { Typography } from "@platform/design-system";

import { useDocumentSelection, useError } from "../hooks";
import { DocLibraryColors } from "../constants";
import { documentTypeToLabel } from "../utils";

import { CustomTooltip } from "./CustomTooltip";
import { DocumentDetailsTooltip } from "./DocumentDetailsTooltip";
import { DocumentCardActionsModal } from "./DocumentCardActionsModal";
import { StatusAlert, type CustomColor } from "./StatusAlert";

import type {
  DocumentStatus,
  DocumentCardProps,
  DocumentLocOption,
} from "../types";

type DocumentCardComponentProps = DocumentCardProps & {
  documentsById: Record<string, DocumentCardProps>;
};

type DocumentCardMetadataProps = {
  effectiveDate?: string | null;
  multipleLoc: DocumentLocOption[] | null;
  isRenewal: boolean;
};

const TOOLTIP_INFO_ICON_SIZE = 16;
const WORD_BREAK = "break-word";

const DocumentCardMetadata = ({
  effectiveDate,
  multipleLoc,
  isRenewal,
}: DocumentCardMetadataProps): React.JSX.Element => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      alignItems: "center",
      mt: 0.75,
      flexWrap: "wrap",
      gap: "2px 8px",
    }}
  >
    <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
      <strong>Effective Date: {effectiveDate ?? "–"}</strong>
    </Typography>

    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: "center",
        whiteSpace: "nowrap",
      }}
    >
      {multipleLoc && multipleLoc.length > 0 && (
        <React.Fragment>
          <Typography variant="caption" color="textSecondary">
            •
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ cursor: "default" }}
          >
            <Typography variant="caption">
              <strong>Multi LOCs</strong>
            </Typography>
            <CustomTooltip
              title="Multi LOCs"
              items={multipleLoc.map((l) => l.label)}
              placement="bottom-start"
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: TOOLTIP_INFO_ICON_SIZE,
                  color: "text.disabled",
                  position: "relative",
                  top: -1,
                }}
              />
            </CustomTooltip>
          </Stack>
        </React.Fragment>
      )}
      {isRenewal && (
        <>
          <Typography variant="caption" color="textSecondary">
            •
          </Typography>
          <Tooltip title="Renewal Rates" placement="bottom-start">
            <CurrencyExchangeIcon
              sx={{
                fontSize: 18,
                color: "text.primary",
                cursor: "default",
              }}
            />
          </Tooltip>
        </>
      )}
    </Stack>
  </Stack>
);

const getStatusConfig = (
  status?: DocumentStatus,
  errorMessage?: string,
  excludedReason?: string,
): {
  label: string;
  message: string;
  color: CustomColor;
  icon: React.ReactNode;
} | null => {
  if (errorMessage) {
    return {
      label: "Error",
      message: errorMessage,
      color: "error",
      icon: (
        <WarningAmberOutlinedIcon
          fontSize="small"
          sx={{ color: DocLibraryColors.errorIcon }}
        />
      ),
    };
  }

  switch (status) {
    case "extracted":
      return {
        label: "Extracted",
        message: "Extracted",
        color: "success",
        icon: (
          <CheckCircleOutlineOutlinedIcon
            fontSize="small"
            sx={{ color: DocLibraryColors.successIcon }}
          />
        ),
      };
    case "excluded":
      return {
        label: "Excluded",
        message: `Excluded - ${excludedReason ? excludedReason : "Other"}`,
        color: "secondary",
        icon: (
          <RemoveCircleOutlineOutlinedIcon
            fontSize="small"
            sx={{ color: DocLibraryColors.excludedIcon }}
          />
        ),
      };
    case "processing":
      return {
        label: "Processing",
        message: "Processing - may take a few minutes",
        color: "primary",
        icon: (
          <SyncIcon
            fontSize="small"
            sx={{ color: DocLibraryColors.processingIcon }}
          />
        ),
      };
    case "error":
      return {
        label: "Error",
        message: errorMessage ?? "Error",
        color: "error",
        icon: (
          <WarningAmberOutlinedIcon
            fontSize="small"
            sx={{ color: DocLibraryColors.errorIcon }}
          />
        ),
      };
    default:
      return null;
  }
};

export const DocumentCard = ({
  id,
  groupType,
  fileName,
  originalFileName,
  documentType,
  pages,
  plans,
  carrierName,
  effectiveDate,
  status,
  multipleLoc,
  errorCode,
  excludedCode,
  documentsById,
  isGroupExcluded,
  brokerDocumentTypes,
  hasRenewalRates,
  metadata: { documentSummary, downloadUrl },
}: DocumentCardComponentProps): React.JSX.Element => {
  const isOtherGroup = groupType === "other";
  const isRenewal =
    Boolean(brokerDocumentTypes?.includes("renewal_rates")) ||
    Boolean(hasRenewalRates);
  const { isSelected, toggle, isBulkActive } = useDocumentSelection();
  const { mapDocumentErrorExcluded } = useError();
  const checked = isSelected(id);

  const errorMessage = errorCode
    ? mapDocumentErrorExcluded(errorCode)
    : undefined;
  const excludedReason = excludedCode
    ? mapDocumentErrorExcluded(excludedCode)
    : undefined;

  const statusConfig = getStatusConfig(status, errorMessage, excludedReason);

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setMenuAnchorEl(null);
  };

  const carrierNameLabel = carrierName || "–";

  const title = isOtherGroup
    ? carrierNameLabel
    : `${documentTypeToLabel(documentType)} - ${carrierNameLabel}`;
  const tooltipTitle = isOtherGroup
    ? carrierName || "Carrier not found"
    : title;

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        display: "grid",
        gridTemplateRows: "auto 2.8em auto 1fr auto",
        rowGap: "4px",
        height: "100%",
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ minWidth: 0 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ minWidth: 0, flex: 1 }}
        >
          {isBulkActive && (
            <Checkbox
              size="small"
              aria-label="Select document"
              checked={checked}
              onChange={() => toggle(id)}
              onClick={(e) => e.stopPropagation()}
              icon={
                <CheckBoxOutlineBlankIcon
                  sx={{
                    color: "grey.100",
                    backgroundColor: "grey.100",
                    borderRadius: 0.75,
                  }}
                />
              }
              checkedIcon={<CheckBoxIcon />}
              sx={{
                p: 0.25,
                "&.Mui-checked": { color: "brandPrimary.main" },
              }}
            />
          )}
          <Tooltip title={tooltipTitle} placement="top">
            <Typography
              variant="body-large"
              noWrap
              sx={{
                cursor: "default",
                flexShrink: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <strong>{title}</strong>
            </Typography>
          </Tooltip>
          {!isOtherGroup && (
            <DocumentDetailsTooltip
              pages={pages}
              summary={documentSummary}
              aiDocType={documentType}
              brokerDocTypes={brokerDocumentTypes}
              url={downloadUrl}
              plans={plans}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: TOOLTIP_INFO_ICON_SIZE,
                  color: DocLibraryColors.infoIcon,
                  cursor: "pointer",
                }}
              />
            </DocumentDetailsTooltip>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ flexShrink: 0 }}
        >
          {statusConfig?.icon}
          <IconButton
            size="small"
            edge="end"
            aria-label="Document actions"
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? "true" : undefined}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Tooltip
        title={
          <Stack spacing={1} sx={{ py: 0.5 }}>
            <Typography variant="caption" sx={{ wordBreak: WORD_BREAK }}>
              {fileName}
            </Typography>
            {fileName !== originalFileName && (
              <>
                <Divider sx={{ borderColor: "white" }} />
                <Typography variant="caption" sx={{ wordBreak: WORD_BREAK }}>
                  Original name: {originalFileName}
                </Typography>
              </>
            )}
          </Stack>
        }
        placement="bottom-start"
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 500,
            },
          },
        }}
      >
        <FileNameTypography
          variant="caption"
          color="textSecondary"
          sx={{ fontWeight: isOtherGroup ? 500 : 0 }}
        >
          {fileName}
        </FileNameTypography>
      </Tooltip>
      {!isOtherGroup && (
        <DocumentCardMetadata
          effectiveDate={effectiveDate}
          multipleLoc={multipleLoc}
          isRenewal={isRenewal}
        />
      )}
      <div />
      {statusConfig && (
        <StatusAlert
          message={statusConfig.message}
          color={statusConfig.color}
        />
      )}

      <DocumentCardActionsModal
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        documentId={id}
        documentsById={documentsById}
        limitActions={groupType === "other" || isGroupExcluded}
      />
    </Card>
  );
};

const FileNameTypography = styled(Typography)(() => ({
  wordBreak: WORD_BREAK,
  overflowWrap: WORD_BREAK,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  lineHeight: "1.4em",
  cursor: "default",
  alignSelf: "start",
}));
